import { useAuth0 } from '@auth0/auth0-react';
import React, { useState } from 'react';
import NoteRoutes from '../../../router/noteRoutes';
import Switch from '@mui/material/Switch/index.js';
import Select, { SelectChangeEvent } from '@mui/material/Select/index.js';
import MenuItem from '@mui/material/MenuItem/index.js';
import type { Note } from '../../../types';
import { toLocalDateString } from '../../../utils/date';

interface LookBackProps {
  timePeriod: string;
  setNotes: (notes: Note[]) => void;
  setnoNotes: (text: string) => void;
  setNoteError: (text: string) => void;
  setTimePeriod: (value: string) => void;
  setNoteView: (value: string) => void;
  getNoteRanges: (
    userid: string,
    todaysDate: string,
    lastWeeksDate: string
  ) => Promise<void>;
  noteview: string;
}

const pad = (n: number): string => String(n).padStart(2, '0');

interface YearAgoRange {
  today: string;
  weekAhead: string;
}

// Computes the "N years ago" date and a week-ahead boundary from it,
// using real Date arithmetic so month/year rollovers (e.g. day 28 + 7)
// and single-digit days are handled correctly — the previous version
// built these as raw, sometimes-unpadded strings, which broke the
// lexicographic date-range comparison whenever today's day-of-month
// was 1 or 2 (producing e.g. "2025-08-8" instead of "2025-08-08").
const computeYearAgoRange = (yearsAgo: number): YearAgoRange => {
  const past = new Date();
  past.setFullYear(past.getFullYear() - yearsAgo);

  const future = new Date(past);
  future.setDate(future.getDate() + 7);

  const today = `${past.getFullYear()}-${pad(past.getMonth() + 1)}-${pad(
    past.getDate()
  )}`;
  const weekAhead = `${future.getFullYear()}-${pad(
    future.getMonth() + 1
  )}-${pad(future.getDate())}`;

  return { today, weekAhead };
};

export const LookBack = (props: LookBackProps) => {
  const [checked, setChecked] = useState(true);
  const { user } = useAuth0();

  const getNoteRangeYear = async () => {
    const userid = user?.sub?.split('|')[1];
    if (!userid) return;
    const { today: todayLastYear, weekAhead: weekAheadLastYear } =
      computeYearAgoRange(1);

    try {
      const res = await NoteRoutes.getNoteRangeYear(
        weekAheadLastYear,
        todayLastYear
      );

      if (Array.isArray(res)) {
        if (res.length < 1) {
          props.setNotes(res);
          props.setnoNotes('No Notes for last year.');
          return;
        }
        props.setNoteError('');
        props.setnoNotes('');
        props.setNotes(res);
      }
    } catch (error) {
      props.setNoteError('Error Getting Notes');
    }
  };

  const onNumericChange = async (checked: boolean, value: string) => {
    props.setTimePeriod(value);
    const userid = user?.sub?.split('|')[1];
    if (checked) {
      if (!userid) return;
      if (value === '1') {
        props.setNoteView('week');
        const todaysDate = toLocalDateString(new Date());
        const myCurrentDate = new Date();
        const myPastDate = new Date(myCurrentDate);
        myPastDate.setDate(myPastDate.getDate() - 7);
        const lastWeeksDate = toLocalDateString(myPastDate);
        props.getNoteRanges(userid, todaysDate, lastWeeksDate);
      }
      if (value === '2') {
        props.setNoteView('weeks');
        const myCurrentDate = new Date();
        const myPastDate = new Date(myCurrentDate);
        myPastDate.setDate(myPastDate.getDate() - 7);
        const eightDaysago = toLocalDateString(myPastDate);
        const pastDate = new Date(myCurrentDate);
        pastDate.setDate(pastDate.getDate() - 14);
        const fourteenDaysAgo = toLocalDateString(pastDate);
        props.getNoteRanges(userid, eightDaysago, fourteenDaysAgo);
      }
      if (value === '3') {
        props.setNoteView('weeks');
        const myCurrentDate = new Date();
        const myPastDate = new Date(myCurrentDate);
        myPastDate.setDate(myPastDate.getDate() - 14);
        const eightDaysago = toLocalDateString(myPastDate);
        const pastDate = new Date(myCurrentDate);
        pastDate.setDate(pastDate.getDate() - 22);
        const fourteenDaysAgo = toLocalDateString(pastDate);
        props.getNoteRanges(userid, eightDaysago, fourteenDaysAgo);
      }
    }
    // year
    if (!checked) {
      if (value === '1') {
        props.setNoteView('year');
        getNoteRangeYear();
      }
      if (value === '2') {
        if (!userid) return;
        props.setNoteView('years');
        const { today: todayLastYear, weekAhead: weekAheadLastYear } =
          computeYearAgoRange(2);
        const res = await NoteRoutes.getNoteRangeYear(
          weekAheadLastYear,
          todayLastYear
        );
        if (Array.isArray(res)) {
          if (res.length < 1) {
            props.setNotes(res);
            props.setnoNotes('No Notes for last year.');
            return;
          }
          props.setNoteError('');
          props.setnoNotes('');
          props.setNotes(res);
        }
      }
      if (value === '3') {
        if (!userid) return;
        props.setNoteView('years');
        const { today: todayLastYear, weekAhead: weekAheadLastYear } =
          computeYearAgoRange(3);
        const res = await NoteRoutes.getNoteRangeYear(
          weekAheadLastYear,
          todayLastYear
        );
        if (Array.isArray(res)) {
          if (res.length < 1) {
            props.setNotes(res);
            props.setnoNotes('No Notes for last year.');
            return;
          }
          props.setNoteError('');
          props.setnoNotes('');
          props.setNotes(res);
        }
      }
    }
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const numericValue = event.target.checked;
    setChecked(event.target.checked);

    if (!numericValue) {
      onNumericChange(numericValue, props.timePeriod);
      if (Number(props.timePeriod) > 1) {
        props.setNoteView('years');
        return;
      }
      props.setNoteView('year');
    }
    if (numericValue) {
      onNumericChange(numericValue, props.timePeriod);
      if (Number(props.timePeriod) > 1) {
        props.setNoteView('weeks');
        return;
      }
      props.setNoteView('week');
    }
  };

  return (
    <div className="lookBackRow">
      <span className="lookBackHeading">Look back</span>
      <Select
        size="small"
        value={props.timePeriod}
        onChange={(e: SelectChangeEvent) => {
          onNumericChange(checked, e.target.value);
        }}
        className="lookBackSelect"
      >
        <MenuItem value="1">1</MenuItem>
        <MenuItem value="2">2</MenuItem>
        <MenuItem value="3">3</MenuItem>
      </Select>

      <span className="lookBackLabel">{props.noteview} ago</span>

      <Switch
        checked={checked}
        onChange={handleChange}
        inputProps={{ 'aria-label': 'controlled' }}
        id="switch"
      />
    </div>
  );
};